/**
 * Color Picker
 *
 * Popover component for selecting category colors from a curated palette
 * that matches the app's earthy theme.
 */

import { Icon, IconKey } from '@/shared/components/icon';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { cn } from '@/shared/utils';
import { useState } from 'react';

// Curated color palette matching app theme
export const CATEGORY_COLORS = [
  // Reds
  { value: '#7F1D1D', name: 'Deep Red' },
  { value: '#991B1B', name: 'Red 700' },
  { value: '#B91C1C', name: 'Red 600' },
  { value: '#DC2626', name: 'Red' },
  { value: '#EF4444', name: 'Light Red' },
  { value: '#F87171', name: 'Soft Red' },
  { value: '#FCA5A5', name: 'Rose Red' },
  { value: '#FECACA', name: 'Pale Red' },

  // Oranges
  { value: '#7C2D12', name: 'Deep Orange' },
  { value: '#9A3412', name: 'Orange 700' },
  { value: '#C2410C', name: 'Orange 600' },
  { value: '#EA580C', name: 'Orange' },
  { value: '#F97316', name: 'Light Orange' },
  { value: '#FB923C', name: 'Soft Orange' },
  { value: '#FDBA74', name: 'Peach' },
  { value: '#FED7AA', name: 'Pale Peach' },

  // Yellows / Ambers
  { value: '#78350F', name: 'Deep Amber' },
  { value: '#92400E', name: 'Amber 700' },
  { value: '#B45309', name: 'Amber 600' },
  { value: '#D97706', name: 'Amber' },
  { value: '#F59E0B', name: 'Golden Amber' },
  { value: '#FBBF24', name: 'Sunflower' },
  { value: '#FCD34D', name: 'Soft Yellow' },
  { value: '#FDE68A', name: 'Pale Yellow' },

  // Greens
  { value: '#14532D', name: 'Deep Green' },
  { value: '#166534', name: 'Green 700' },
  { value: '#15803D', name: 'Green 600' },
  { value: '#16A34A', name: 'Green' },
  { value: '#22C55E', name: 'Bright Green' },
  { value: '#4ADE80', name: 'Mint' },
  { value: '#86EFAC', name: 'Soft Mint' },
  { value: '#BBF7D0', name: 'Pale Mint' },

  // Teals
  { value: '#134E4A', name: 'Deep Teal' },
  { value: '#115E59', name: 'Teal 700' },
  { value: '#0F766E', name: 'Teal 600' },
  { value: '#0D9488', name: 'Teal' },
  { value: '#14B8A6', name: 'Bright Teal' },
  { value: '#2DD4BF', name: 'Turquoise' },
  { value: '#5EEAD4', name: 'Soft Aqua' },
  { value: '#99F6E4', name: 'Pale Aqua' },

  // Blues
  { value: '#1E3A8A', name: 'Deep Blue' },
  { value: '#1D4ED8', name: 'Blue 700' },
  { value: '#2563EB', name: 'Blue 600' },
  { value: '#3B82F6', name: 'Blue' },
  { value: '#60A5FA', name: 'Sky Blue' },
  { value: '#93C5FD', name: 'Soft Sky' },
  { value: '#BFDBFE', name: 'Ice Blue' },
  { value: '#DBEAFE', name: 'Pale Ice' },

  // Purples
  { value: '#4C1D95', name: 'Deep Purple' },
  { value: '#5B21B6', name: 'Purple 700' },
  { value: '#6D28D9', name: 'Purple 600' },
  { value: '#7C3AED', name: 'Purple' },
  { value: '#8B5CF6', name: 'Lavender Purple' },
  { value: '#A78BFA', name: 'Soft Lavender' },
  { value: '#C4B5FD', name: 'Pastel Lavender' },
  { value: '#DDD6FE', name: 'Pale Lavender' },

  // Pinks
  { value: '#831843', name: 'Deep Pink' },
  { value: '#9D174D', name: 'Pink 700' },
  { value: '#BE185D', name: 'Pink 600' },
  { value: '#DB2777', name: 'Pink' },
  { value: '#EC4899', name: 'Bright Pink' },
  { value: '#F472B6', name: 'Soft Pink' },
  { value: '#F9A8D4', name: 'Blush' },
  { value: '#FBCFE8', name: 'Pale Blush' }
];

interface ColorPickerProps {
  value: string | null | undefined;
  onChange: (value: string) => void;
}

interface InlineColorPickerProps {
  value: string | null | undefined;
  onChange: (value: string) => void;
  className?: string;
}

function getCategoryColor(name: string) {
  const index = Math.abs(
    name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  ) % CATEGORY_COLORS.length;

  return CATEGORY_COLORS[index];
}

export function InlineColorPicker({ value, onChange, className }: InlineColorPickerProps) {
  return (
    <div className={cn('grid grid-cols-8 gap-3 p-3 max-h-[200px] overflow-y-auto rounded-md bg-card', className)}>
      {CATEGORY_COLORS.map(({ value: colorValue, name }) => (
        <button
          key={colorValue}
          type='button'
          title={name}
          className={cn(
            'size-7 rounded-full transition-transform hover:scale-110 flex items-center justify-center mx-auto cursor-pointer',
            value === colorValue && 'ring-2 ring-offset-2 ring-accent'
          )}
          style={{ backgroundColor: colorValue }}
          onClick={() => onChange(colorValue)}
        >
          {value === colorValue && <Icon icon={IconKey.Check} size={16} className='text-white drop-shadow-md' />}
        </button>
      ))}
    </div>
  );
}

export function ColorPicker({ value, onChange }: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedColor = getCategoryColor(value ?? '');

  return (
    <>
      <Button type='button' variant='outline' className='w-full justify-start gap-2' onClick={() => setIsOpen(true)}>
        {value ? (
          <div className='w-4 h-4 rounded-full border border-border/50' style={{ backgroundColor: value }} />
        ) : (
          <Icon icon={IconKey.Palette} size={16} className='text-text/50' />
        )}
        <span className='text-text/70'>{selectedColor?.name || 'Select color'}</span>
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className='sm:max-w-[320px]'>
          <DialogHeader>
            <DialogTitle>Select Color</DialogTitle>
          </DialogHeader>
          <div className='grid grid-cols-4 gap-3 py-4'>
            {CATEGORY_COLORS.map(({ value: colorValue, name }) => (
              <button
                key={colorValue}
                type='button'
                title={name}
                className={cn(
                  'size-12 rounded-lg transition-transform hover:scale-110 flex items-center justify-center',
                  value === colorValue && 'ring-2 ring-offset-2 ring-text'
                )}
                style={{ backgroundColor: colorValue }}
                onClick={() => {
                  onChange(colorValue);
                  setIsOpen(false);
                }}
              >
                {value === colorValue && <Icon icon={IconKey.Check} size={20} className='text-white drop-shadow-md' />}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
